import React, { useCallback, useEffect, useMemo, useState } from "react";
import { adminApiRequest } from "../utils/adminApi";
import Modal from "./Modal";

export default function Reviews() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [reviewToDelete, setReviewToDelete] = useState(null);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [actionType, setActionType] = useState(null);

  const sortedReviews = useMemo(() => {
    return [...reviews].sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at)
    );
  }, [reviews]);

  useEffect(() => {
    if (!success) return;

    const timer = setTimeout(() => {
      setSuccess("");
    }, 3000);

    return () => clearTimeout(timer);
  }, [success]);

  useEffect(() => {
    if (!error) return;

    const timer = setTimeout(() => {
      setError("");
    }, 4000);

    return () => clearTimeout(timer);
  }, [error]);

  const formatDateTime = (date) => {
    if (!date) return "-";
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return "-";

    return d.toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const renderStars = (rating) => {
    const count = Number(rating) || 0;
    return "★".repeat(count) + "☆".repeat(5 - count);
  };

  const getErrorMessage = (err, fallback) => {
    if (err.message === "REQUEST_TIMEOUT") {
      return "Request timed out. Please try again.";
    }
    if (err.message === "NETWORK_ERROR") {
      return "Network error. Please check your connection.";
    }
    if (err.message === "UNAUTHORIZED" || err.message === "SESSION_EXPIRED") {
      return "Your session has expired. Please sign in again.";
    }
    return err.message || fallback;
  };

  const loadReviews = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const res = await adminApiRequest({
        script: "list_reviews",
      });

      if (!res?.success) {
        throw new Error(res?.message || "Failed to load reviews.");
      }

      setReviews(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError(getErrorMessage(err, "Unable to load reviews."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadReviews();
  }, [loadReviews]);

  const handleToggleApproval = async (item) => {
    const nextStatus = Number(item.is_approved) === 1 ? 0 : 1;

    try {
      setActionLoadingId(item.id);
      setActionType("approve");
      setError("");
      setSuccess("");

      const res = await adminApiRequest({
        script: "toggle_review_approval",
        data: {
          id: item.id,
          is_approved: nextStatus,
        },
      });

      if (!res?.success) {
        throw new Error(res?.message || "Failed to update review status.");
      }

      setSuccess(
        nextStatus === 1
          ? "Review approved successfully."
          : "Review unapproved successfully."
      );

      setReviews((prev) =>
        prev.map((review) =>
          Number(review.id) === Number(item.id)
            ? { ...review, is_approved: nextStatus }
            : review
        )
      );
    } catch (err) {
      setError(getErrorMessage(err, "Unable to update review status."));
    } finally {
      setActionLoadingId(null);
      setActionType(null);
    }
  };

  const openDeleteModal = (item) => {
    if (actionLoadingId) return;
    setReviewToDelete(item);
  };

  const closeDeleteModal = () => {
    if (actionType === "delete") return;
    setReviewToDelete(null);
  };

  const handleDelete = async (e) => {
    e.preventDefault();

    if (!reviewToDelete) return;

    try {
      setActionLoadingId(reviewToDelete.id);
      setActionType("delete");
      setError("");
      setSuccess("");

      const res = await adminApiRequest({
        script: "delete_review",
        data: { id: reviewToDelete.id },
      });

      if (!res?.success) {
        throw new Error(res?.message || "Failed to delete review.");
      }

      setSuccess("Review deleted successfully.");

      setReviews((prev) =>
        prev.filter(
          (review) => Number(review.id) !== Number(reviewToDelete.id)
        )
      );

      setReviewToDelete(null);
    } catch (err) {
      setError(getErrorMessage(err, "Unable to delete review."));
    } finally {
      setActionLoadingId(null);
      setActionType(null);
    }
  };

  return (
    <section className="h-full min-h-0">
      <div className="flex h-full min-h-0 flex-col rounded-3xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-200 p-5">
          <h2 className="text-2xl font-semibold text-slate-900">Reviews</h2>
          <p className="mt-1 text-sm text-slate-500">
            Manage customer reviews and approval status.
          </p>
        </div>

        {(error || success) && (
          <div className="px-5 pt-4">
            {error && (
              <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {error}
              </div>
            )}

            {success && (
              <div className="rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
                {success}
              </div>
            )}
          </div>
        )}

        <div className="min-h-0 flex-1 overflow-auto p-5">
          {loading ? (
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-10 text-center text-sm text-slate-500">
              Loading reviews...
            </div>
          ) : sortedReviews.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-10 text-center">
              <p className="text-sm text-slate-500">No reviews found.</p>
            </div>
          ) : (
            <div className="overflow-hidden rounded-2xl border border-slate-200">
              <div className="overflow-x-auto">
                <table className="min-w-full text-left">
                  <thead className="bg-slate-50">
                    <tr className="text-sm text-slate-600">
                      <th className="px-4 py-3 font-semibold">Name</th>
                      <th className="px-4 py-3 font-semibold">Rating</th>
                      <th className="px-4 py-3 font-semibold">Review</th>
                      <th className="px-4 py-3 font-semibold">Status</th>
                      <th className="px-4 py-3 font-semibold">Posted</th>
                      <th className="px-4 py-3 font-semibold text-right">
                        Actions
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-slate-200 bg-white">
                    {sortedReviews.map((item) => {
                      const isRowLoading = actionLoadingId === item.id;
                      const isApproveLoading =
                        isRowLoading && actionType === "approve";
                      const isDeleteLoading =
                        isRowLoading && actionType === "delete";

                      return (
                        <tr
                          key={item.id}
                          className={`align-top ${
                            isRowLoading ? "opacity-70" : ""
                          }`}
                        >
                          <td className="px-4 py-4 text-sm font-medium text-slate-900">
                            {item.name}
                          </td>

                          <td className="px-4 py-4 text-sm text-amber-500">
                            <div className="font-medium">
                              {renderStars(item.rating)}
                            </div>
                            <div className="text-xs text-slate-500">
                              {item.rating}/5
                            </div>
                          </td>

                          <td className="px-4 py-4 text-sm text-slate-700">
                            <div className="max-w-xl whitespace-normal break-words">
                              {item.review}
                            </div>
                          </td>

                          <td className="px-4 py-4">
                            <span
                              className={`inline-flex rounded-full px-3 py-1 text-xs font-medium ${
                                Number(item.is_approved) === 1
                                  ? "bg-green-100 text-green-700"
                                  : "bg-amber-100 text-amber-700"
                              }`}
                            >
                              {Number(item.is_approved) === 1
                                ? "Approved"
                                : "Pending"}
                            </span>
                          </td>

                          <td className="px-4 py-4 text-sm text-slate-700">
                            {formatDateTime(item.created_at)}
                          </td>

                          <td className="px-4 py-4">
                            <div className="flex justify-end gap-2">
                              <button
                                onClick={() => handleToggleApproval(item)}
                                disabled={isRowLoading}
                                className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition ${
                                  Number(item.is_approved) === 1
                                    ? "border border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100"
                                    : "border border-green-300 bg-green-50 text-green-700 hover:bg-green-100"
                                } ${
                                  isRowLoading
                                    ? "cursor-not-allowed opacity-70"
                                    : ""
                                }`}
                              >
                                {isApproveLoading && (
                                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"></span>
                                )}
                                {Number(item.is_approved) === 1
                                  ? "Unapprove"
                                  : "Approve"}
                              </button>

                              <button
                                onClick={() => openDeleteModal(item)}
                                disabled={isRowLoading}
                                className={`flex items-center gap-2 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm font-medium text-red-700 transition hover:bg-red-100 ${
                                  isRowLoading
                                    ? "cursor-not-allowed opacity-70"
                                    : ""
                                }`}
                              >
                                {isDeleteLoading && (
                                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"></span>
                                )}
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {reviewToDelete && (
        <Modal
          title="Delete Review"
          onClose={closeDeleteModal}
          onSubmit={handleDelete}
          submitButtonText="Delete Review"
          showCancel={true}
          showSubmitButton={true}
          submitButtonClass="bg-red-600 hover:bg-red-700 text-white"
          disableSubmit={actionType === "delete"}
        >
          <div className="space-y-3 text-sm text-slate-700">
            <p>
              Are you sure you want to delete this review by{" "}
              <span className="font-semibold text-slate-900">
                {reviewToDelete.name}
              </span>
              ?
            </p>

            <div className="rounded-lg bg-slate-50 p-3 text-slate-600">
              <div className="text-amber-500">
                {renderStars(reviewToDelete.rating)}
              </div>
              <div className="mt-2 whitespace-normal break-words">
                {reviewToDelete.review}
              </div>
            </div>

            <p className="text-xs text-slate-500">
              This action cannot be undone.
            </p>
          </div>
        </Modal>
      )}
    </section>
  );
}